import type { FormEvent, ReactNode, RefObject } from 'react';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import { PublicFooter, PublicTopBar } from '../../shell';
import { CheckRow, CodeSlots, Field, TextWell } from '../../shell/fields';
import shell from '../../shell/shell.module.css';
import styles from './apply.module.css';

/** Start (spec §5.1): what it takes, the two acknowledgements, one brand button. The applicant is already signed in. */
export function StartScreen({
  unitLabel,
  privacy,
  electronic,
  disabled,
  starting,
  onSubmit,
}: {
  unitLabel: string;
  privacy: { checked: boolean; url: string; onChange: (checked: boolean) => void };
  electronic: { checked: boolean; url: string; onChange: (checked: boolean) => void };
  disabled: boolean;
  starting: boolean;
  onSubmit: () => void;
}) {
  return (
    <section className={styles.screen} aria-labelledby="apply-start-title">
      <h1 id="apply-start-title" className={styles.question}>Apply for {unitLabel}</h1>
      <p className={styles.lead}>About 10 minutes. Your answers save as you go, so you can stop and finish later.</p>
      <div className={styles.handy}>
        <p className={styles.handyTitle}>Have these handy</p>
        <ul>
          <li><span className={styles.tile}><UnitfieldIcon name="briefcase" size={17} aria-hidden="true" /></span>Where you work and what you earn</li>
          <li><span className={styles.tile}><UnitfieldIcon name="home" size={17} aria-hidden="true" /></span>Your addresses from the last few years</li>
          <li><span className={styles.tile}><UnitfieldIcon name="document" size={17} aria-hidden="true" /></span>Pay stubs or an offer letter, if you have them</li>
        </ul>
      </div>
      <form
        className={styles.fields}
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className={styles.checks}>
          <CheckRow checked={privacy.checked} disabled={disabled} onChange={privacy.onChange}>
            I acknowledge the{' '}
            <a href={privacy.url} target="_blank" rel="noreferrer">Applicant Privacy Notice</a>.
          </CheckRow>
          <CheckRow checked={electronic.checked} disabled={disabled} onChange={electronic.onChange}>
            I consent to use electronic records and signatures under the{' '}
            <a href={electronic.url} target="_blank" rel="noreferrer">Electronic Records Consent</a>.
          </CheckRow>
        </div>
        <div className={styles.acts}>
          <button type="submit" className={cn(shell.btn, shell.btnBrand, shell.btnFull)} disabled={disabled || !privacy.checked || !electronic.checked}>
            {starting ? 'Starting…' : 'Start application'}
          </button>
        </div>
      </form>
    </section>
  );
}

/** Code (spec §5.2): the address once, six wells, and a way to ask again. */
export function CodeScreen({
  email,
  code,
  onCodeChange,
  disabled,
  verifying,
  resending,
  onVerify,
  onResend,
  onChangeEmail,
}: {
  email: string;
  code: string;
  onCodeChange: (value: string) => void;
  disabled: boolean;
  verifying: boolean;
  resending: boolean;
  onVerify: () => void;
  onResend: () => void;
  onChangeEmail: () => void;
}) {
  return (
    <section className={styles.screen} aria-labelledby="apply-code-title">
      <h1 id="apply-code-title" className={styles.question}>Check your email</h1>
      <p className={styles.lead}>
        Your verification request was accepted. Check <b className={styles.strong}>{email}</b> for a 6-digit code.
      </p>
      <form
        className={styles.fields}
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onVerify();
        }}
      >
        <label className="sr-only" htmlFor="apply-code">Six-digit code</label>
        <CodeSlots id="apply-code" value={code} onChange={onCodeChange} disabled={disabled} autoFocus inputMode="numeric" autoComplete="one-time-code" pushPasswordManagerStrategy="none" />
        <p className={styles.note}>
          Didn’t get it?{' '}
          <button type="button" className={styles.textLink} disabled={disabled || resending} onClick={onResend}>Send a new code</button>
          {' · '}
          <button type="button" className={styles.textLink} disabled={disabled} onClick={onChangeEmail}>Use a different email</button>
        </p>
        <div className={styles.acts}>
          <button type="submit" className={cn(shell.btn, shell.btnBrand, shell.btnFull)} disabled={disabled}>
            {verifying ? 'Checking…' : 'Continue'}
          </button>
        </div>
      </form>
    </section>
  );
}

/** One message with its actions, for the closed, unavailable and confirm states. */
export function TaskScreen({ title, children, actions, top }: { title: string; children?: ReactNode; actions?: ReactNode; top?: ReactNode }) {
  return (
    <div className={cn(shell.page, styles.taskPage)}>
      <PublicTopBar trailing={top} />
      <main className={styles.task}>
        <h1 className={styles.question}>{title}</h1>
        {children ? <div className={styles.taskBody}>{children}</div> : null}
        {actions ? <div className={styles.taskActions}>{actions}</div> : null}
      </main>
      <PublicFooter />
    </div>
  );
}

/** The hosted sign-in page: the business's top bar and footer around one screen. Same frame as a task screen. */
export function SignInFrame({ children }: { children: ReactNode }) {
  return (
    <div className={cn(shell.page, styles.taskPage)}>
      <PublicTopBar />
      <main className={styles.task}>{children}</main>
      <PublicFooter />
    </div>
  );
}

/** Sign-in, step one: the address and nothing else. The copy is the same whether or not an account exists. */
export function SignInEmailScreen({
  title,
  lead,
  email,
  onEmailChange,
  emailRef,
  disabled,
  sending,
  onSubmit,
  extra,
  alternative,
  error,
}: {
  title: string;
  lead: string;
  email: string;
  onEmailChange: (value: string) => void;
  emailRef?: RefObject<HTMLInputElement | null>;
  disabled: boolean;
  sending: boolean;
  onSubmit: () => void;
  /** Rendered above the button: the bot check, when the API asks for one. */
  extra?: ReactNode;
  /** Another way in, offered after the email button under a plain "or" (Continue with Google). The email field stays the primary path. */
  alternative?: ReactNode;
  error?: string | null;
}) {
  return (
    <section className={styles.screen} aria-labelledby="sign-in-title">
      <h1 id="sign-in-title" className={styles.question}>{title}</h1>
      <p className={styles.lead}>{lead}</p>
      <form
        className={styles.fields}
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <Field label="Email address" htmlFor="email">
          <TextWell
            ref={emailRef}
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            autoFocus
            placeholder="you@example.com"
            disabled={disabled}
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            invalid={Boolean(error)}
          />
        </Field>
        {error ? <p role="alert" className={styles.note}>{error}</p> : null}
        {extra}
        <div className={styles.acts}>
          <button type="submit" className={cn(shell.btn, shell.btnBrand, shell.btnFull)} disabled={disabled}>
            {sending ? 'Sending code…' : 'Email me a code'}
          </button>
        </div>
      </form>
      {alternative ? (
        <div className={styles.alternative}>
          <p className={styles.alternativeOr}>or</p>
          {alternative}
        </div>
      ) : null}
    </section>
  );
}
